import { useCallback, useMemo } from 'react'

import { HouseAvailabilityUnionOutputDto } from '../../../lib/graphql'
import {
  convertIsoDateArrayToTimezonedDate,
  convertIsoToTimezonedDate,
  convertIsoToTimezonedEndDate,
  convertTimezonedDateToIsoAtMidnight,
} from '../../../utils/date-helpers'

export interface IDates {
  startDate?: Date
  endDate?: Date
}

interface IUseTimezoneConverter {
  startDate?: Date
  endDate?: Date
  setDate: (props: IDates) => void
  houseAvailabilities: HouseAvailabilityUnionOutputDto[]
}

/**
 * input / Output dates conversion helper
 *
 * Input dates are expected to be UTC dates at midnight ex: `{startDate: '2021-09-01T00:00:00.000Z', endDate: '2021-10-01T00:00:00.000Z'}`
 * Output dates are timezoned dates at midnight for startDate, at 23:59:59 for endDate. ex: `{ startDate: '2021-09-01T00:00:00.000+02:00', endDate: '2021-10-01T23:59:59.000+02:00'}`
 *
 * The returned dates are timezoned dates and the return `setDate` is expected to receive timezoned dates which it will convert to UTC dates before forwarding to original callback.
 *
 * @returns {IUseTimezoneConverter} startDate, endDate, setDate
 */
export default function useTimezoneConverter({
  startDate,
  endDate,
  setDate,
  houseAvailabilities = [],
}: IUseTimezoneConverter): IUseTimezoneConverter {
  const startDateTZ = useMemo(
    () => (startDate ? convertIsoToTimezonedDate(startDate) : undefined),
    [startDate],
  )
  const endDateTZ = useMemo(
    () => (endDate ? convertIsoToTimezonedEndDate(endDate) : undefined),
    [endDate],
  )
  const setDateTZ = useCallback(({ startDate, endDate }: IDates) => {
    setDate({
      startDate: startDate
        ? convertTimezonedDateToIsoAtMidnight(startDate)
        : undefined,
      endDate: endDate
        ? convertTimezonedDateToIsoAtMidnight(endDate)
        : undefined,
    })
  }, [])

  const houseAvailabilitiesTZ = useMemo(
    () => convertIsoDateArrayToTimezonedDate(houseAvailabilities),
    [houseAvailabilities],
  )

  return {
    startDate: startDateTZ,
    endDate: endDateTZ,
    setDate: setDateTZ,
    houseAvailabilities: houseAvailabilitiesTZ,
  }
}
